import pricingLogo from "assets/images/merchandise/pricing-logo.svg";
import advertisingLogo from "assets/images/merchandise/advertising-logo.svg";
import contentLogo from "assets/images/merchandise/content-logo.svg";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks";
import OnboardingWorkflowProgress from "../OnboardingWorkflowProgress";
import type { ReactNode } from "react";
import { useModuleEnabled } from "redux/selectors/modules";
import { buttonRecipe } from "../../core/basic/Button";
import { PageContainer } from "../../core/basic/PageContainer";
import { css } from "../../../../styled-system/css";
import { Center, Flex, VStack } from "../../../../styled-system/jsx";
import Card from "../../core/basic/Card";

interface CTAProps {
  title: string;
  icon: string;
  children: ReactNode;
  to: string;
  toText: string;
}

interface EmptyStateProps {
  type: string;
}

const CTA = ({ title, icon, children, to, toText }: CTAProps) => {
  return (
    <Card className={css({ flex: 1, minWidth: "300px", maxWidth: "400px" })}>
      <VStack gap="medium" width="100%" padding="medium" fontSize={"medium"}>
        <img
          src={icon}
          className={css({
            display: "block",
            width: "180px",
            height: "180px",
          })}
        />
        <h3 className={css({ margin: 0, fontWeight: "normal" })}>{title}</h3>
        <div className={css({ minHeight: "200px" })}>{children}</div>
        <Link to={to} className={buttonRecipe()}>
          {toText}
        </Link>
      </VStack>
    </Card>
  );
};

const EmptyState = ({ type }: EmptyStateProps) => {
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const showWorkflowProgressBar = useModuleEnabled("WORKFLOW_PROGRESS");

  return (
    <PageContainer gap="medium" alignItems={"center"} padding="large">
      <h3 className={css({ margin: 0 })}>Welcome to Trellis</h3>

      {type === "NO_DATA" && (
        <>
          <p className={css({ margin: 0 })}>Please connect to your marketplace to get started.</p>

          {channel === "walmart" ?
            <Link to="/user/walmartsummary" className={buttonRecipe()}>
              Walmart Connect
            </Link>
          : <Link to="/user/amzsummary" className={buttonRecipe()}>
              Amazon Connect
            </Link>
          }
        </>
      )}
      {type === "COLLECTING_DATA" && (
        <>
          <p className={css({ textAlign: "center" })}>
            We're collecting data and building your dashboard.
            <br />
            In the meantime explore the Trellis platform:
          </p>
          {showWorkflowProgressBar && <OnboardingWorkflowProgress showLabel={false} />}
        </>
      )}
      {channel === "walmart" ?
        <Center>
          <CTA
            title={"Advertising"}
            to={"/user/advertising/dashboard"}
            toText={"Create a media plan"}
            icon={advertisingLogo}
          >
            <p>Trellis’ bidding algorithm dramatically reduces wasted ad spend by:</p>
            <ul>
              <li>Harvesting and implementing keywords across the shoppers funnel</li>
              <li>Adjusting bids daily, and</li>
              <li>Reallocating budgets.</li>
            </ul>
          </CTA>
        </Center>
      : <Flex gap="large" direction={{ base: "column", lg: "row" }} justifyContent={"center"}>
          <CTA
            title={"Advertising"}
            to={"/user/advertising/dashboard"}
            toText={"Create a media plan"}
            icon={advertisingLogo}
          >
            <p>Trellis’ bidding algorithm dramatically reduces wasted ad spend by:</p>
            <ul>
              <li>Harvesting and implementing keywords across the shoppers funnel</li>
              <li>Adjusting bids daily, and</li>
              <li>Reallocating budgets.</li>
            </ul>
          </CTA>
          <CTA title={"Dynamic Pricing"} to={"/user/_pricing/dashboard"} toText={"Enroll Products"} icon={pricingLogo}>
            <p>By tracking and understanding your users' behavior, our tool can optimize for:</p>
            <ul>
              <li>Sales growth</li>
              <li>Profit margins, or</li>
              <li>Net profit.</li>
            </ul>
          </CTA>
          <CTA title={"Content"} to={"/user/content/quality/org"} toText={"View Content"} icon={contentLogo}>
            <p>
              We provide content suggestions by analyzing your titles, A+ Content, images, price, and more to improve
              your:
            </p>
            <ul>
              <li>Search Engine Optimization (SEO)</li>
              <li>The appeal of your listing, and</li>
              <li>Your listing's effectiveness at converting users.</li>
            </ul>
          </CTA>
        </Flex>
      }
    </PageContainer>
  );
};

export default EmptyState;
